import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { STATE_PAGES } from "../data/states";
import { JOURNEYS, FILTERS, TRAVELLER_META } from "../data/journeys";
import { STATES as STATE_GRID } from "../data/content";

export default function State() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const s = STATE_PAGES[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
        if (s) document.title = `${s.name} — enei.in`;
    }, [s]);

    if (!s) {
        return (
            <div className="App bg-paper grain">
                <Navbar />
                <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
                    <p className="font-serif italic text-forest text-3xl mb-4">
                        That state has wandered off the map.
                    </p>
                    <Link
                        to="/#states"
                        className="text-terracotta uppercase tracking-[0.22em] text-[12px] border-b border-terracotta pb-1"
                    >
                        Back to all states
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const journeysHere = JOURNEYS.filter((j) => j.state === slug);

    // Sibling states for "Next" navigation
    const order = STATE_GRID.map((x) => x.slug);
    const idx = order.indexOf(slug);
    const next = STATE_PAGES[order[(idx + 1) % order.length]];
    const prev = STATE_PAGES[order[(idx - 1 + order.length) % order.length]];

    const speakAbout = () => {
        sessionStorage.setItem(
            "enei_prefill",
            JSON.stringify({
                states: [slug],
                message: `I'd like to plan a journey through ${s.name}.`,
            })
        );
        navigate("/#plan");
    };

    return (
        <div className="App bg-paper grain">
            <Navbar />

            {/* Cinematic hero */}
            <section
                data-testid="state-hero"
                className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-forest"
            >
                <img
                    src={s.heroImage}
                    alt={`${s.name} — ${s.tagline}`}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover animate-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/95 via-forest/45 to-forest/35" />
                <div className="absolute inset-0 bg-gradient-to-b from-forest/40 via-transparent to-transparent" />

                <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
                    <Link
                        to="/#states"
                        data-testid="back-to-states"
                        className="text-[11px] uppercase tracking-[0.28em] text-paper/80 hero-shadow mb-8 hover:text-marigold transition-colors w-fit"
                    >
                        ← Eight states
                    </Link>
                    <p className="text-marigold text-xs md:text-sm uppercase tracking-[0.32em] hero-shadow mb-6">
                        {String(idx + 1).padStart(2, "0")} / 08 · Capital: {s.capital}
                    </p>
                    <h1
                        data-testid="state-name"
                        className="font-serif text-paper hero-shadow text-5xl sm:text-7xl md:text-[110px] lg:text-[140px] leading-[0.95] tracking-tighter font-light"
                    >
                        {s.name}
                    </h1>
                    <p className="mt-6 font-serif italic text-marigold text-2xl md:text-4xl hero-shadow">
                        {s.tagline}
                    </p>
                </div>
            </section>

            {/* Sensory intro */}
            <section className="bg-paper py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <aside className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
                            — First, the feeling
                        </p>
                    </aside>
                    <div className="col-span-12 md:col-span-8">
                        <p
                            data-testid="state-intro"
                            className="dropcap text-forest text-lg md:text-[19px] leading-relaxed"
                        >
                            {s.intro}
                        </p>
                    </div>
                </div>
            </section>

            {/* Five places that matter */}
            <section className="bg-stone py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
                        — Five places that matter
                    </p>
                    <h2 className="font-serif text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight max-w-3xl mb-16">
                        Not a list. <em className="italic">A shortlist.</em>
                    </h2>

                    <ol className="space-y-10 md:space-y-12">
                        {s.places.map((p, i) => (
                            <li
                                key={p.name}
                                data-testid={`place-${i}`}
                                className="grid grid-cols-12 gap-4 md:gap-8 pb-10 md:pb-12 border-b border-forest/15 last:border-0"
                            >
                                <div className="col-span-12 md:col-span-1">
                                    <p className="font-serif italic text-terracotta text-2xl md:text-3xl">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                </div>
                                <div className="col-span-12 md:col-span-4">
                                    <h3 className="font-serif text-forest text-2xl md:text-4xl font-light leading-tight">
                                        {p.name}
                                    </h3>
                                </div>
                                <div className="col-span-12 md:col-span-7">
                                    <p className="text-slatewarm text-[15px] md:text-base leading-relaxed">
                                        {p.line}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* When to go */}
            <section className="bg-paper py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
                            — When to go
                        </p>
                        <h2 className="font-serif text-forest text-3xl md:text-4xl font-light leading-tight max-w-md">
                            The state has its own calendar. <em className="italic">Ours simply listens.</em>
                        </h2>
                    </div>
                    <ul className="col-span-12 md:col-span-8 space-y-6">
                        {s.seasons.map((sn, i) => (
                            <li
                                key={i}
                                data-testid={`season-${i}`}
                                className="grid grid-cols-12 gap-4 md:gap-8 pb-6 border-b border-forest/15 last:border-0"
                            >
                                <p className="col-span-12 md:col-span-3 font-serif italic text-terracotta text-lg md:text-xl">
                                    {sn.months}
                                </p>
                                <p className="col-span-12 md:col-span-9 text-slatewarm text-base md:text-[17px] leading-relaxed">
                                    {sn.note}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Food */}
            <section className="bg-stone py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
                            — Eat with both hands
                        </p>
                        <h2 className="font-serif text-forest text-3xl md:text-4xl font-light leading-tight max-w-md">
                            What grandmothers serve, <em className="italic">when no tourist is looking.</em>
                        </h2>
                    </div>
                    <ul className="col-span-12 md:col-span-8 space-y-5">
                        {s.food.map((f, i) => (
                            <li
                                key={i}
                                data-testid={`food-${i}`}
                                className="flex gap-5 items-baseline border-b border-forest/15 pb-5"
                            >
                                <span className="font-serif italic text-marigold text-base shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="text-forest text-[16px] md:text-lg leading-relaxed">
                                    {f}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Practical */}
            <section className="bg-paper py-24 md:py-28">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
                            — Practical, woven in
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-8 space-y-10">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] text-slatewarm mb-3">
                                Getting there
                            </p>
                            <p
                                data-testid="getting-there"
                                className="text-forest text-[16px] md:text-lg leading-relaxed"
                            >
                                {s.getting_there}
                            </p>
                        </div>
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] text-slatewarm mb-3">
                                Permits
                            </p>
                            <p
                                data-testid="permits"
                                className="text-forest text-[16px] md:text-lg leading-relaxed"
                            >
                                {s.permits}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Journeys here */}
            {journeysHere.length > 0 && (
                <section className="bg-stone py-24 md:py-32">
                    <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
                            — {journeysHere.length} curated {journeysHere.length === 1 ? "journey" : "journeys"} through {s.name}
                        </p>
                        <h2 className="font-serif text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight max-w-3xl mb-16">
                            How we'd <em className="italic">walk you through</em> it.
                        </h2>

                        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                            {journeysHere.map((j) => (
                                <Link
                                    key={j.slug}
                                    to={`/journeys/${j.slug}`}
                                    data-testid={`state-journey-${j.slug}`}
                                    className="group block"
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden bg-paper">
                                        <img
                                            src={j.image}
                                            alt={j.title}
                                            loading="lazy"
                                            decoding="async"
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                        />
                                    </div>
                                    <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-terracotta">
                                        {TRAVELLER_META[j.traveller].title} · {j.duration}
                                    </p>
                                    <h3 className="mt-2 font-serif text-forest text-2xl md:text-3xl font-light leading-tight group-hover:text-terracotta transition-colors">
                                        {j.title}
                                    </h3>
                                    <p className="mt-3 font-serif italic text-slatewarm text-base md:text-lg leading-snug">
                                        {j.oneLiner}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Curator CTA */}
            <section className="bg-forest text-paper py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-7">
                        <p className="text-xs uppercase tracking-[0.28em] text-marigold mb-6">
                            — Speak with the curator
                        </p>
                        <h2 className="font-serif text-paper text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight">
                            Let's plan something through {s.name},
                            <br className="hidden md:block" />
                            <em className="italic text-marigold">at your pace.</em>
                        </h2>
                        <button
                            onClick={speakAbout}
                            data-testid="speak-about-state"
                            className="mt-10 inline-flex items-center gap-3 bg-paper text-forest px-8 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-marigold transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Begin a {s.name} conversation <span aria-hidden>→</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Sibling navigation */}
            <section className="bg-paper py-16 border-t border-forest/10">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-2 gap-6">
                    <Link
                        to={`/states/${prev.slug}`}
                        data-testid="prev-state"
                        className="group block"
                    >
                        <p className="text-[11px] uppercase tracking-[0.22em] text-slatewarm mb-2">
                            ← Previous
                        </p>
                        <p className="font-serif text-forest text-2xl md:text-3xl font-light group-hover:text-terracotta transition-colors">
                            {prev.name}
                        </p>
                        <p className="text-slatewarm text-sm italic mt-1">{prev.tagline}</p>
                    </Link>
                    <Link
                        to={`/states/${next.slug}`}
                        data-testid="next-state"
                        className="group block text-right"
                    >
                        <p className="text-[11px] uppercase tracking-[0.22em] text-slatewarm mb-2">
                            Next →
                        </p>
                        <p className="font-serif text-forest text-2xl md:text-3xl font-light group-hover:text-terracotta transition-colors">
                            {next.name}
                        </p>
                        <p className="text-slatewarm text-sm italic mt-1">{next.tagline}</p>
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
